// Add this debounce function at the beginning of your file
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Centralized locale string access helper
// Locale strings are now loaded in layout/theme.liquid via window.theme.strings

const getLocaleString = (key, replacements = {}) => {
  let string;

  // Get from centralized theme strings
  if (window.theme?.strings?.[key]) {
    string = window.theme.strings[key];
  } else {
    // Fallback to key if string not found
    console.warn(`Locale string not found: ${key}`);
    string = key;
  }

  // Replace placeholders with actual values
  Object.keys(replacements).forEach(placeholder => {
    string = string.replace(`{{ ${placeholder} }}`, replacements[placeholder]);
  });

  return string;
};

// Helper function to get template content
const getTemplateContent = (templateId) => {
  const template = document.getElementById(templateId);
  if (!template) {
    console.error(`Template with id "${templateId}" not found`);
    return null;
  }
  return template.content.cloneNode(true);
};

const elements = {
  cartButton: document.getElementById('cart-button'), // Back to single cart button
  closeCartButton: document.getElementById('close-cart-menu'),
  filtersButton: document.getElementById('filters-button'),
  closeFiltersButton: document.getElementById('close-filters-drawer'),
  cartMenu: document.getElementById('cart-menu'),
  filtersDrawer: document.getElementById('filters-drawer'),
  overlay: document.getElementById('overlay'),
  desktopMenuOverlay: document.getElementById('desktop-menu-overlay'),
  addToCartForm: document.getElementById('add-to-cart-form'),
  sizeSelect: document.getElementById('size-select'),
  addToCartButton: document.getElementById('add-to-cart-btn'),
  colorOptions: document.querySelector('.color-options'),
  logo: document.getElementById('logo'),
};

let productSplide;
let lastValidImageIndex = 0;

// Reusable functions
const toggleMenu = (menuElement, open) => {
  if (!menuElement) return;

  // Handle cart and filters (slide-in drawers)
  if (menuElement === elements.cartMenu) {
    menuElement.classList.toggle('active', open);
  } else {
    // For filters drawer (if it exists)
    const translateClass = '-translate-x-full';
    menuElement.classList.toggle(translateClass, !open);
  }

  // Only manage overlay if it exists and no other menus are open
  if (elements.overlay) {
    const anyMenuOpen = open ||
      (elements.cartMenu && elements.cartMenu.classList.contains('active')) ||
      (elements.filtersDrawer && !elements.filtersDrawer.classList.contains('-translate-x-full'));

    // Overlay has fixed z-50 - no dynamic switching needed

    elements.overlay.classList.toggle('opacity-0', !anyMenuOpen);
    elements.overlay.classList.toggle('pointer-events-none', !anyMenuOpen);
  }

  // Disable body scrolling when any drawer is open
  const anyDrawerOpen =
    (elements.cartMenu && elements.cartMenu.classList.contains('active')) ||
    (elements.filtersDrawer && !elements.filtersDrawer.classList.contains('-translate-x-full'));

  if (anyDrawerOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const fetchCartData = () => fetch('/cart.js').then(response => response.json());

const closeAllMenus = () => {
  console.log('Closing all menus');
  if (elements.cartMenu) toggleMenu(elements.cartMenu, false);
  if (elements.filtersDrawer) toggleMenu(elements.filtersDrawer, false);
  // Close desktop menu if it exists
  if (window.desktopMenu && window.desktopMenu.isOpen) {
    window.desktopMenu.hideMenu();
  }
  // Close mobile menu if it exists
  if (window.mobileMenu && window.mobileMenu.isOpen()) {
    window.mobileMenu.close();
  }
};



const updateCart = async (cart) => {
  console.log('🛒 updateCart called with', cart.items.length, 'items');

  // Update cart UI components
  updateMenuCart(cart);
  updateMainCart(cart);
  updateCartCount(cart.item_count);

  // Update free shipping progress if function exists
  if (window.updateFreeShippingProgress) {
    window.updateFreeShippingProgress(cart);
  }
};

const updateCartContents = async () => {
  try {
    const cart = await fetchCartData();
    await updateCart(cart);
  } catch (error) {
    console.error('Error updating cart contents:', error);
  }
};

const debouncedUpdateCartContents = debounce(updateCartContents, 300);

const updateCartItemElement = (cartItem, item, prefix) => {
  const cartItemElement = cartItem.querySelector('.cart-item');
  cartItemElement.dataset.key = item.key;
  cartItemElement.dataset.unitPrice = item.price;

  const img = cartItem.querySelector('img');
  img.src = item.image;
  img.alt = item.title;

  const titleLink = cartItem.querySelector('.cart-item-title-link');
  titleLink.href = item.url;
  titleLink.textContent = item.product_title;

  const variantTitle = cartItem.querySelector('.cart-item-variant');
  if (item.variant_title && item.variant_title !== 'Default Title') {
    variantTitle.textContent = item.variant_title;
  } else {
    variantTitle.remove();
  }

  const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
  const inputId = `${prefix}quantity-${item.key}`;
  quantityInput.value = item.quantity;
  quantityInput.dataset.key = item.key;
  quantityInput.dataset.currentQuantity = item.quantity;
  quantityInput.id = inputId;

  // Update label's for attribute
  const label = cartItem.querySelector('label');
  if (label) {
    label.setAttribute('for', inputId);
  }

  // Update quantity button states
  const wrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
  if (wrapper) {
    updateQuantityButtonStates(wrapper);
  }

  const price = cartItem.querySelector('.cart-item-price');
  price.textContent = Shopify.formatMoney(item.line_price);

  // Update remove button data attributes
  const removeForm = cartItem.querySelector('.cart-item-remove form');
  const removeInput = removeForm?.querySelector('input[name="id"]');
  const removeButton = cartItem.querySelector('[data-cart-remove]');

  if (removeInput) {
    removeInput.value = item.key;
  }

  if (removeButton) {
    removeButton.dataset.itemTitle = item.product_title;
    removeButton.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
  }
};

const updateQuantityButtonStates = (wrapper) => {
  const input = wrapper.querySelector('.cart-item-quantity-input');
  const decreaseButton = wrapper.querySelector('.cart-item-quantity-decrease');
  const quantity = parseInt(input.value);

  if (decreaseButton) {
    decreaseButton.disabled = quantity <= 1;
    decreaseButton.classList.toggle('opacity-30', quantity <= 1);
    decreaseButton.classList.toggle('cursor-not-allowed', quantity <= 1);
  }
};

const handleCartQuantityChange = (event) => {
  const target = event.target;
  const button = target.closest('.cart-item-quantity-decrease, .cart-item-quantity-increase');

  if (button) {
    event.preventDefault();
    const wrapper = button.closest('.cart-item-quantity-wrapper');
    const input = wrapper.querySelector('.cart-item-quantity-input');
    const key = input.dataset.key;
    let quantity = parseInt(input.value);

    if (button.classList.contains('cart-item-quantity-decrease')) {
      quantity = quantity > 1 ? quantity - 1 : 1; // Don't go below 1
    } else if (button.classList.contains('cart-item-quantity-increase')) {
      quantity += 1;
    }

    input.value = quantity;

    // Update button states
    updateQuantityButtonStates(wrapper);

    // Update price immediately in both main cart and menu cart
    updateItemPrice(key, quantity);

    // Update cart on server and refresh totals
    updateCartItemQuantity(key, quantity);
  }
};

const handleCartItemRemoval = async (event) => {
  const target = event.target;
  const button = target.closest('[data-cart-remove]');

  // Only proceed if this is actually a cart removal button click
  if (!button) {
    return;
  }

  console.log('Cart removal handler called', event.target);
  console.log('Remove button found:', button);

  event.preventDefault();

  const itemTitle = button.dataset.itemTitle || 'this item';
  const form = button.closest('form');
  const itemKey = form?.querySelector('input[name="id"]')?.value;

  if (!itemKey) {
    console.error('No item key found for removal');
    return;
  }

  const confirmed = confirm(`Remove ${itemTitle} from cart?`);

  if (confirmed) {
    updateCartItemQuantity(itemKey, 0);
  }
};

const updateItemPrice = (key, quantity) => {
  const cartItems = document.querySelectorAll(`.cart-item[data-key="${key}"]`);

  cartItems.forEach(cartItem => {
    const priceElement = cartItem.querySelector('.cart-item-price');
    const unitPrice = parseFloat(cartItem.dataset.unitPrice);
    if (priceElement && !isNaN(unitPrice)) {
      const newPrice = unitPrice * quantity;
      priceElement.textContent = Shopify.formatMoney(newPrice);
    }

    const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
    if (quantityInput) {
      quantityInput.value = quantity;
    }

    // Update quantity button states
    const wrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
    if (wrapper) {
      updateQuantityButtonStates(wrapper);
    }
  });

  updateTotalPriceEstimate();

  // Update free shipping progress with estimated total
  updateFreeShippingProgressEstimate();
};

const updateTotalPriceEstimate = () => {
  const cartItems = document.querySelectorAll('#cart-items-container .cart-item');
  let totalPrice = 0;

  cartItems.forEach(item => {
    const price = parseFloat(item.dataset.unitPrice);
    const quantity = parseInt(item.querySelector('.cart-item-quantity-input').value);
    totalPrice += price * quantity;
  });

  const cartTotal = document.getElementById('cart-total');
  if (cartTotal) {
    cartTotal.textContent = Shopify.formatMoney(totalPrice);
  }
};

const updateFreeShippingProgressEstimate = () => {
  // Calculate estimated total from current cart items
  const cartItems = document.querySelectorAll('.cart-item[data-key]');
  let totalPrice = 0;
  let itemCount = 0;

  cartItems.forEach(item => {
    const price = parseFloat(item.dataset.unitPrice);
    const quantity = parseInt(item.querySelector('.cart-item-quantity-input').value);
    if (!isNaN(price) && !isNaN(quantity)) {
      totalPrice += price * quantity;
      itemCount += quantity;
    }
  });

  // Create a fake cart object for the progress bar update
  const estimatedCart = {
    total_price: totalPrice,
    item_count: itemCount
  };

  // Update free shipping progress if function exists
  if (window.updateFreeShippingProgress) {
    window.updateFreeShippingProgress(estimatedCart);
  }
};

const updateCartItemQuantity = (key, quantity) => {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: key, quantity: quantity })
  })
    .then(response => response.json())
    .then(cart => {
      if (quantity === 0) {
        // Remove the item from both main cart and menu cart
        const mainCartItem = document.querySelector(`#cart-items-container .cart-item[data-key="${key}"]`);
        const menuCartItem = document.querySelector(`#menu-cart-items-container .cart-item[data-key="${key}"]`);

        if (mainCartItem) mainCartItem.remove();
        if (menuCartItem) menuCartItem.remove();
      }

      // Update cart contents (this will update totals and any discrepancies)
      updateCartContents();

      // Check if cart is empty and update UI accordingly
      if (cart.item_count === 0) {
        updateEmptyCartState();
      }
    })
    .catch(error => console.error('Error updating cart:', error));
};

const updateEmptyCartState = () => {
  const mainCartContainer = document.getElementById('cart-items-container');
  const menuCartContainer = document.getElementById('menu-cart-items-container');

  if (mainCartContainer) {
    const mainEmptyTemplate = getTemplateContent('cart-empty-main-template');
    if (mainEmptyTemplate) {
      mainCartContainer.innerHTML = '';
      mainCartContainer.appendChild(mainEmptyTemplate);
    }
  }

  if (menuCartContainer) {
    const menuEmptyTemplate = getTemplateContent('cart-empty-menu-template');
    if (menuEmptyTemplate) {
      menuCartContainer.innerHTML = '';
      menuCartContainer.appendChild(menuEmptyTemplate);
    }
  }

  // Hide checkout buttons and totals
  const checkoutButton = document.querySelector('button[name="checkout"]');
  const additionalCheckoutButtons = document.querySelector('.additional-checkout-buttons');
  const cartTotalContainer = document.querySelector('.cart-total-container');
  const cartNoteSection = document.querySelector('.cart-note-section');

  if (checkoutButton) checkoutButton.style.display = 'none';
  if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = 'none';
  if (cartTotalContainer) cartTotalContainer.style.display = 'none';
  if (cartNoteSection) cartNoteSection.style.display = 'none';
};

const selectColor = (button) => {
  // Remove 'selected' class from all color buttons
  document.querySelectorAll('.color-option').forEach(btn => btn.classList.remove('selected'));

  // Add 'selected' class to clicked button
  button.classList.add('selected');

  // Get selected color
  const selectedColor = button.getAttribute('data-color');

  // Update available sizes based on selected color
  updateAvailableSizes(selectedColor);

  // Check button availability
  const addToCartButton = document.getElementById('add-to-cart-btn');
  if (addToCartButton) {
    // Check if we have a valid variant selection now
    const selectedVariantId = window.VariantSelector ?
      window.VariantSelector.getSelectedVariantId('main-product') : null;

    if (!selectedVariantId) {
      // No valid variant, show appropriate message
      const sizeSelector = document.querySelector('[data-form-id="main-product"].variant-selector');
      if (sizeSelector) {
        // Size selection required
        setAddToCartButtonError(addToCartButton);
      } else {
        // No sizes available for this color
        addToCartButton.textContent = getLocaleString('product.sold_out');
        addToCartButton.disabled = true;
      }
    } else {
      // Valid variant available
      resetAddToCartButton(addToCartButton);
    }
  }

  // Find the first available variant with this color and update the image
  const variants = getProductVariants();
  if (variants.length > 0) {
    const selectedVariant = variants.find(variant => variant.options.includes(selectedColor) && variant.available);
    if (selectedVariant) {
      updateProductImage(selectedVariant);
      // Update URL with selected color
      updateURL(selectedColor);
    }
  }
};

// Helper function to get product variants
const getProductVariants = () => {
  const variantsElement = document.getElementById('product-variants');
  if (!variantsElement) return [];

  try {
    return JSON.parse(variantsElement.textContent);
  } catch (error) {
    console.error('Error parsing product variants:', error);
    return [];
  }
};

// Helper function to find variant by options
const findVariantByOptions = (color, size) => {
  const variants = getProductVariants();

  if (color && size) {
    return variants.find(variant =>
      variant.options.includes(color) && variant.options.includes(size) && variant.available
    );
  } else if (color) {
    return variants.find(variant =>
      variant.options.includes(color) && variant.available
    );
  } else if (size) {
    return variants.find(variant =>
      variant.options.includes(size) && variant.available
    );
  }

  return variants.find(variant => variant.available) || variants[0];
};

const addToCart = (event) => {
  event.preventDefault();

  console.log('Add to cart function called');

  const form = document.getElementById('add-to-cart-form');
  const colorOptions = document.querySelector('.color-options');
  const addToCartButton = document.getElementById('add-to-cart-btn');
  const formId = form?.dataset.formId || 'main-product';

  if (!form) {
    console.error('Add to cart form not found');
    return;
  }

  console.log('Form elements found:', {
    form: !!form,
    colorOptions: !!colorOptions,
    addToCartButton: !!addToCartButton,
    formId: formId
  });

  console.log('Starting add to cart process');

  // Reset button appearance if validations pass
  if (addToCartButton) resetAddToCartButton(addToCartButton);

  // Get variant ID from VariantSelector or hidden input for single variants
  let selectedVariantId = window.VariantSelector ? window.VariantSelector.getSelectedVariantId(formId) : null;

  // For single variant products, check for hidden input
  if (!selectedVariantId) {
    const hiddenVariantInput = form.querySelector('.variant-id-input');
    if (hiddenVariantInput && hiddenVariantInput.value) {
      selectedVariantId = hiddenVariantInput.value;
    }
  }

  if (!selectedVariantId) {
    console.log('No variant selected');
    if (addToCartButton) setAddToCartButtonError(addToCartButton);
    return;
  }

  console.log('Selected variant ID:', selectedVariantId);

  console.log('🚀 Add to cart initiated');

  // Set loading state
  if (addToCartButton) {
    addToCartButton.textContent = getLocaleString('cart.adding');
    addToCartButton.disabled = true;
  }

  // Use VariantSelector's addToCart method if available, otherwise fallback to manual approach
  const addToCartPromise = window.VariantSelector ?
    window.VariantSelector.addToCart(formId, selectedVariantId, 1) :
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1
      })
    })
      .then(response => response.json())
      .then(data => {
        console.log('Product added to cart:', data);
        return fetch('/cart.js');
      })
      .then(response => response.json())
      .then(async cart => {
        await updateCart(cart);
        return cart;
      });

  addToCartPromise
    .then(cart => {
      console.log('Cart updated:', cart);

      // Navigate to cart page after adding item
      window.location.href = '/cart';
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert(getLocaleString('cart.add_to_cart_error'));
    })
    .finally(() => {
      // Reset button state
      if (addToCartButton) resetAddToCartButton(addToCartButton);
    });
};

function updateProductImageBasedOnSelection() {
  const sizeSelect = document.getElementById('size-select');
  const colorOptions = document.querySelector('.color-options');

  if (!sizeSelect) return;

  const selectedSize = sizeSelect.value;
  const selectedColor = document.querySelector('.color-option.selected')?.getAttribute('data-color');

  if (!selectedSize && !selectedColor) return;

  const selectedVariant = findVariantByOptions(selectedColor, selectedSize);
  if (selectedVariant) {
    updateProductImage(selectedVariant);
  }
}

// Event listeners setup
const setupEventListeners = () => {
  if (elements.cartButton) {
    elements.cartButton.addEventListener('click', async (e) => {
      e.stopPropagation();
      e.preventDefault();
      console.log('Cart button clicked, opening cart drawer');

      toggleMenu(elements.cartMenu, true);
    });
  }
  if (elements.closeCartButton) {
    elements.closeCartButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.cartMenu, false);
    });
  }

  // Filter drawer event listeners
  if (elements.filtersButton) {
    elements.filtersButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.filtersDrawer, true);
    });
  }
  if (elements.closeFiltersButton) {
    elements.closeFiltersButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.filtersDrawer, false);
    });
  }

  // Only add overlay listener if overlay exists and is the main shared overlay
  if (elements.overlay) {
    elements.overlay.addEventListener('click', closeAllMenus);
  }

  if (elements.desktopMenuOverlay) {
    elements.desktopMenuOverlay.addEventListener('click', () => {
      if (window.desktopMenu && window.desktopMenu.isOpen) {
        window.desktopMenu.hideMenu();
      }
    });
  }

  // ESC key to close menus
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllMenus();
    }
  });

  document.addEventListener('click', handleCartQuantityChange);

  // Handle cart item removal
  document.addEventListener('click', handleCartItemRemoval);

  if (elements.addToCartForm) {
    elements.addToCartForm.addEventListener('submit', addToCart);
  }

  if (elements.sizeSelect) {
    elements.sizeSelect.addEventListener('change', () => {
      resetAddToCartButton(elements.addToCartButton);
      updateProductImageBasedOnSelection();
    });
  }

  if (elements.colorOptions) {
    elements.colorOptions.addEventListener('click', (event) => {
      if (event.target.classList.contains('color-option')) {
        selectColor(event.target);
      }
    });
  }
};

// Initialize quantity button states for existing cart items
const initializeQuantityButtonStates = () => {
  const quantityWrappers = document.querySelectorAll('.cart-item-quantity-wrapper');
  quantityWrappers.forEach(wrapper => {
    updateQuantityButtonStates(wrapper);
  });
};

// Initialization
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing cart');

  // Debug element initialization
  console.log('Elements found:', {
    cartButton: !!elements.cartButton,
    cartMenu: !!elements.cartMenu,
    overlay: !!elements.overlay
  });

  setupEventListeners();
  selectColorFromURL();
  initializeQuantityButtonStates();

  const cartItemsContainer = document.getElementById('cart-items-container');
  if (cartItemsContainer && !cartItemsContainer.querySelector('#cart-items')) {
    updateCartContents();
  }

  fetchCartData()
    .then(async cart => {
      // Cart data loaded
    })
    .catch(error => console.error('Error fetching cart data on page load:', error));
});

function updateMenuCart(cart) {
  console.log('📦 Updating menu cart with', cart.items.length, 'items');
  const menuCartItemsContainer = document.getElementById('menu-cart-items-container');
  const menuCartItemTemplate = document.getElementById('menu-cart-item-template');
  const menuCartFooter = document.querySelector('.menu-cart-footer');
  const cartNotesSection = document.querySelector('.cart-notes-section');

  if (!menuCartItemsContainer) {
    console.error('Menu cart items container not found');
    return;
  }

  // Clear existing items
  menuCartItemsContainer.innerHTML = '';

  if (cart.items.length === 0) {
    // Cart is empty, show the empty cart message
    const menuEmptyTemplate = getTemplateContent('cart-empty-menu-template');
    if (menuEmptyTemplate) {
      menuCartItemsContainer.innerHTML = '';
      menuCartItemsContainer.appendChild(menuEmptyTemplate);
    }
    // Hide the menu cart footer and notes section
    if (menuCartFooter) {
      menuCartFooter.style.display = 'none';
    }
    if (cartNotesSection) {
      cartNotesSection.style.display = 'none';
    }
  } else {
    // Cart has items, proceed with normal update
    const div = document.createElement('div');
    div.id = 'menu-cart-items';
    div.className = 'cart-items-list';

    // Process cart items
    for (const item of cart.items) {
      const cartItem = menuCartItemTemplate.content.cloneNode(true);
      updateCartItemElement(cartItem, item, 'menu-');
      div.appendChild(cartItem);
    }

    menuCartItemsContainer.appendChild(div);

    // Update total in footer
    const menuCartTotal = document.getElementById('menu-cart-total');
    if (menuCartTotal) {
      menuCartTotal.textContent = Shopify.formatMoney(cart.total_price);
    }

    // Show the menu cart footer and notes section
    if (menuCartFooter) {
      menuCartFooter.style.display = 'block';
    }
    if (cartNotesSection) {
      cartNotesSection.style.display = 'block';

      // Update the cart note textarea with current cart note
      const cartNoteTextarea = cartNotesSection.querySelector('#cart-note-drawer');
      if (cartNoteTextarea) {
        cartNoteTextarea.value = cart.note || '';
      }
    }
  }
}

function updateMainCart(cart) {
  const cartItemsContainer = document.getElementById('cart-items-container');
  const cartItemTemplate = document.getElementById('cart-item-template');
  const cartTotal = document.getElementById('cart-total');
  const cartForm = document.querySelector('form[action="/cart"]');

  if (cartItemsContainer) {
    // Check if the cart is already populated
    if (cartItemsContainer.querySelector('#cart-items') && cart.items.length > 0) {
      // Cart is already populated, just update quantities and totals
      updateExistingCartItems(cart);
      return;
    }

    cartItemsContainer.innerHTML = '';

    if (cart.items.length === 0) {
      // Cart is empty
      const mainEmptyTemplate = getTemplateContent('cart-empty-main-template');
      if (mainEmptyTemplate) {
        cartItemsContainer.innerHTML = '';
        cartItemsContainer.appendChild(mainEmptyTemplate);
      }

      // Hide the cart total, checkout button, and other elements
      if (cartTotal) cartTotal.closest('.cart-total-container').style.display = 'none';
      const checkoutButton = cartForm.querySelector('button[name="checkout"]');
      if (checkoutButton) checkoutButton.style.display = 'none';

      // Hide additional checkout buttons if they exist
      const additionalCheckoutButtons = cartForm.querySelector('.additional-checkout-buttons');
      if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = 'none';
    } else {
      // Cart has items
      const div = document.createElement('div');
      div.id = 'cart-items';
      div.className = 'cart-items-list';

      // Process cart items (main cart doesn't need add-ons in most cases)
      cart.items.forEach(item => {
        const cartItem = cartItemTemplate.content.cloneNode(true);
        updateCartItemElement(cartItem, item, 'cart-');
        div.appendChild(cartItem);
      });

      cartItemsContainer.appendChild(div);

      // Show and update the cart total
      if (cartTotal) {
        cartTotal.textContent = Shopify.formatMoney(cart.total_price);
        cartTotal.closest('.cart-total-container').style.display = '';
      }

      // Show the checkout button
      const checkoutButton = cartForm.querySelector('button[name="checkout"]');
      if (checkoutButton) checkoutButton.style.display = '';

      // Show additional checkout buttons if they exist
      const additionalCheckoutButtons = cartForm.querySelector('.additional-checkout-buttons');
      if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = '';
    }
  }
}

function updateExistingCartItems(cart) {
  cart.items.forEach(item => {
    const cartItem = document.querySelector(`.cart-item[data-key="${item.key}"]`);
    if (cartItem) {
      const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
      if (quantityInput) {
        quantityInput.value = item.quantity;
      }
      updateItemPrices(item.key, item.quantity);
    }
  });

  // Update total
  const cartTotal = document.getElementById('cart-total');
  if (cartTotal) {
    cartTotal.textContent = Shopify.formatMoney(cart.total_price);
  }
}

function updateItemPriceAndTotal(item, newQuantity) {
  const newLinePrice = item.price * newQuantity;
  const cartItem = document.querySelector(`.cart-item[data-key="${item.key}"]`);
  const priceElement = cartItem.querySelector('.cart-item-price');
  priceElement.textContent = Shopify.formatMoney(newLinePrice);

  // Update the total price
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const cartTotal = document.getElementById('cart-total');
      if (cartTotal) {
        cartTotal.textContent = Shopify.formatMoney(cart.total_price);
      }
    })
    .catch(error => console.error('Error updating total price:', error));
}

function updateCartCount(count) {
  const cartCountElement = document.querySelector('#cart-button .cart-count');

  if (cartCountElement) {
    cartCountElement.textContent = count;
    if (count > 0) {
      cartCountElement.classList.remove('hidden');
    } else {
      cartCountElement.classList.add('hidden');
    }
  }
}

// Updated function to update URL
function updateURL(color) {
  if (history.pushState) {
    const formattedColor = color.toLowerCase().replace(/\s+/g, '-');
    const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?color=' + encodeURIComponent(formattedColor);
    window.history.pushState({ path: newurl }, '', newurl);
  }
}

// Updated function to select color from URL parameter
function selectColorFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const color = urlParams.get('color');
  if (color) {
    const formattedColor = color.replace(/-/g, ' ');
    const colorButton = document.querySelector(`.color-option[data-color="${formattedColor}"]`);
    if (colorButton) {
      selectColor(colorButton);
    }
  }
}

function updateAvailableSizes(color) {
  const sizeSelect = document.getElementById('size-select');
  const variants = getProductVariants();

  if (!sizeSelect || variants.length === 0) return;

  // Store the current selection
  const currentSelection = sizeSelect.value;

  // Get size options for the selected color
  const availableSizes = new Set();
  const unavailableSizes = new Set();

  variants.forEach(variant => {
    if (variant.options.includes(color)) {
      // Find the size option (the option that isn't the color)
      const sizeIndex = variant.option_names.findIndex(name =>
        name !== "Color" && name !== "Farbe" && name !== "Colour"
      );
      if (sizeIndex !== -1) {
        const size = variant.options[sizeIndex];
        if (variant.available) {
          availableSizes.add(size);
        } else {
          unavailableSizes.add(size);
        }
      }
    }
  });

  // Update the size select using the global function
  if (window.populateSizeSelect) {
    window.populateSizeSelect(availableSizes, unavailableSizes);
  }

  // Restore the previous selection if it's still available
  if (Array.from(sizeSelect.options).some(option => option.value === currentSelection && !option.disabled)) {
    sizeSelect.value = currentSelection;
  } else {
    sizeSelect.value = ""; // Reset to default option if previous selection is not available
  }

  // Find the first available variant with this color and update the image
  const selectedVariant = variants.find(variant => variant.options.includes(color) && variant.available);
  if (selectedVariant) {
    updateProductImage(selectedVariant);
  }
}

// New function to set error state on Add to Cart button
function setAddToCartButtonError(button) {
  button.textContent = getLocaleString('product.please_select');
  button.style.backgroundColor = 'red';
  button.style.color = 'white';
  button.disabled = true;
}

// New function to reset Add to Cart button
function resetAddToCartButton(button) {
  button.textContent = getLocaleString('cart.add_to_cart');
  button.style.backgroundColor = '';
  button.style.color = '';
  button.disabled = false;
}



Shopify.formatMoney = function (cents, format = "€{{amount}}") {
  var value = (cents / 100).toFixed(2).replace('.', ',');
  return format.replace("{{amount}}", value);
};

function findVariantImageIndex(variant) {
  const variantImage = variant.featured_image;
  if (!variantImage) {
    return -1;
  }

  const imagesElement = document.getElementById('product-images');
  if (!imagesElement) {
    return -1;
  }

  const images = JSON.parse(imagesElement.textContent);


  // Try to find an exact match
  let index = images.findIndex(image => image === variantImage.src);

  // If no exact match, try to find a partial match
  if (index === -1) {
    index = images.findIndex(image => variantImage.src.includes(image) || image.includes(variantImage.src));
  }

  return index;
}

function updateProductImage(variant) {
  const imageIndex = findVariantImageIndex(variant);
  if (window.productSplide && imageIndex !== -1) {
    window.productSplide.go(imageIndex);
    lastValidImageIndex = imageIndex;
  } else {
    console.log('No matching image found or Splide instance not available. Keeping current slide.');
    if (window.productSplide) {
      window.productSplide.go(lastValidImageIndex);
    }
  }
}

console.log('Cart JavaScript loaded');



// Function to update cart note
function updateCartNote(note) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ note: note })
  })
    .then(response => response.json())
    .then(cart => {
      console.log('Cart note updated:', cart.note);
    })
    .catch(error => console.error('Error updating cart note:', error));
}

// Listen for variant changes and update price display
document.addEventListener('variantChanged', function (event) {
  const { variant, formId } = event.detail;

  // Only update price for main product page
  if (formId !== 'main-product' || !variant) return;

  // Find the price container in product-meta
  const priceContainer = document.querySelector('.product-meta-price');
  if (!priceContainer) return;

  // Format prices
  const price = Shopify.formatMoney(variant.price);
  const compareAtPrice = variant.compare_at_price ? Shopify.formatMoney(variant.compare_at_price) : null;

  // Build new price HTML
  let priceHTML = '';
  if (compareAtPrice && variant.compare_at_price > variant.price) {
    priceHTML = `
      <span class="line-through opacity-50">${compareAtPrice}</span>
      <span>${price}</span>
    `;
  } else {
    priceHTML = `<span>${price}</span>`;
  }

  // Update the price display
  priceContainer.innerHTML = priceHTML;
});

// Make cart functions globally accessible
window.updateCart = updateCart;
window.updateCartContents = updateCartContents;
window.updateMenuCart = updateMenuCart;
window.updateMainCart = updateMainCart;
window.updateCartCount = updateCartCount;
window.updateCartNote = updateCartNote;
window.toggleCartDrawer = () => toggleMenu(elements.cartMenu, true);
window.closeAllMenus = closeAllMenus;